import { useEffect, useState, useCallback, useRef } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { openFile, downloadFile, DEFAULT_DOC_TYPE } from "@/lib/files";
import { invalidateFileBlob } from "@/lib/blobCache";
import { toast } from "sonner";

// Encapsulates all file state + upload/open/download/delete logic for a client.
export function useClientFiles(clientId) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadItems, setUploadItems] = useState([]); // [{id, name, percent}]
  const [syncing, setSyncing] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [pendingFiles, setPendingFiles] = useState(null); // files awaiting a doc-type choice
  const fileInputRef = useRef(null);

  const load = useCallback(() => {
    api.get(`/clients/${clientId}/files`).then((r) => setFiles(r.data));
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const upload = useCallback(async (fileList, docType = DEFAULT_DOC_TYPE) => {
    const picked = Array.from(fileList || []);
    if (!picked.length) return;
    const MAX_BYTES = 100 * 1024 * 1024; // 100 MB
    const CHUNK_SIZE = 1024 * 1024;      // 1 MB (bypasses the gateway request-size limit)

    const tooBig = picked.filter((f) => f.size > MAX_BYTES);
    tooBig.forEach((f) =>
      toast.error(`"${f.name}" is ${(f.size / 1048576).toFixed(0)} MB — too large. The maximum is 100 MB, please reduce the file size and try again.`));
    const valid = picked.filter((f) => f.size <= MAX_BYTES);
    if (!valid.length) return;

    const batch = valid.map((f, idx) => ({ id: `${Date.now()}-${idx}-${f.name}`, name: f.name, percent: 0 }));
    const setPercent = (id, percent) =>
      setUploadItems((prev) => prev.map((u) => (u.id === id ? { ...u, percent } : u)));

    const uploadOne = async (f, itemId) => {
      const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const total = Math.max(1, Math.ceil(f.size / CHUNK_SIZE));
      for (let i = 0; i < total; i++) {
        const blob = f.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("chunk_index", i);
        fd.append("total_chunks", total);
        fd.append("filename", f.name);
        fd.append("content_type", f.type || "application/octet-stream");
        fd.append("doc_type", docType);
        fd.append("chunk", blob, f.name);
        await api.post(`/clients/${clientId}/files/chunk`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            const frac = e.total ? e.loaded / e.total : 1;
            setPercent(itemId, Math.min(99, Math.round(((i + frac) / total) * 100)));
          },
        });
      }
      setPercent(itemId, 100);
    };

    setUploading(true);
    setUploadItems(batch);
    let ok = 0;
    for (let bi = 0; bi < valid.length; bi++) {
      try { await uploadOne(valid[bi], batch[bi].id); ok += 1; }
      catch (err) { toast.error(`${valid[bi].name}: ${formatApiErrorDetail(err.response?.data?.detail) || "upload failed"}`); }
    }
    if (ok) toast.success(ok > 1 ? `${ok} files uploaded` : "File uploaded");
    setUploadItems([]);
    setUploading(false);
    load();
  }, [clientId, load]);

  const syncFromSharePoint = useCallback(async () => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/sync-sharepoint`);
      const parts = [];
      if (data.imported) parts.push(`${data.imported} imported`);
      if (data.link_removed) parts.push(`${data.link_removed} link${data.link_removed > 1 ? "s" : ""} removed in SharePoint`);
      if (data.relinked) parts.push(`${data.relinked} re-linked`);
      toast.success(parts.length ? `Synced: ${parts.join(", ")}` : "Already up to date with SharePoint");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Sync failed");
    } finally { setSyncing(false); }
  }, [clientId, load]);

  // Stage picked/dropped files so the UI can ask for a document type first.
  const stageFiles = useCallback((fileList) => {
    const picked = Array.from(fileList || []);
    if (picked.length) setPendingFiles(picked);
  }, []);

  const confirmUpload = useCallback(async (docType) => {
    const toUpload = pendingFiles;
    setPendingFiles(null);
    await upload(toUpload, docType);
  }, [pendingFiles, upload]);

  const cancelUpload = useCallback(() => setPendingFiles(null), []);

  const onInputChange = useCallback((e) => {
    stageFiles(e.target.files);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [stageFiles]);

  const dragHandlers = {
    onDragOver: (e) => { e.preventDefault(); setDragActive(true); },
    onDragEnter: (e) => { e.preventDefault(); setDragActive(true); },
    onDragLeave: (e) => { e.preventDefault(); setDragActive(false); },
    onDrop: (e) => { e.preventDefault(); setDragActive(false); stageFiles(e.dataTransfer.files); },
  };

  const open = useCallback(async (f) => {
    try { await openFile(f.id, f.original_filename); } catch { toast.error("Could not open file"); }
  }, []);

  const download = useCallback(async (f) => {
    try { await downloadFile(f.id, f.original_filename); } catch { toast.error("Could not download file"); }
  }, []);

  const recategorize = useCallback(async (id, docType) => {
    try {
      await api.patch(`/files/${id}/doc-type`, { doc_type: docType });
      setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, doc_type: docType } : f)));
      toast.success("Category updated");
    } catch { toast.error("Could not update category"); }
  }, []);

  const remove = useCallback(async (id) => {
    try {
      await api.delete(`/files/${id}`);
      invalidateFileBlob(id);
      toast.success("File removed");
      load();
    } catch { toast.error("Could not remove file"); }
  }, [load]);

  return { files, uploading, uploadItems, syncing, syncFromSharePoint, dragActive, fileInputRef, onInputChange, dragHandlers,
           pendingFiles, confirmUpload, cancelUpload, open, download, recategorize, remove };
}
